import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Printer } from 'lucide-react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { ApiError } from '@/api/errors'
import { ErrorState, LoadingState, NotFoundState } from '@/components/feedback/States'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { receiptQuery } from '@/features/finance/api'
import { InvoiceStatusBadge } from '@/features/finance/components/InvoiceStatusBadge'
import { localizePeriodLabel, methodLabel } from '@/features/finance/status'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatDate, formatDateTime, formatMoney } from '@/utils/format'
import { sectionLabel } from '@/features/academic/sectionLabel'

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className="text-right font-medium text-ink">{value}</dd>
    </div>
  )
}

export function ReceiptPage() {
  const { t } = useTranslation('finance')
  const id = Number(useParams().id)
  const { data, isPending, error, refetch } = useQuery(receiptQuery(id))
  useDocumentTitle(data ? t('Receipt {{number}}', { number: data.payment.receiptNumber }) : 'Receipt')

  if (isPending) return <LoadingState />
  if (error)
    return error instanceof ApiError && error.status === 404 ? (
      <NotFoundState resource="receipt" homeHref="/admin/finance/payments" />
    ) : (
      <ErrorState onRetry={() => void refetch()} />
    )

  const { payment: p, invoice: inv, school, student } = data
  const money = (v: string) => formatMoney(v, p.currency)

  return createPortal(
    <div
      data-print-document
      className="fixed inset-0 z-40 overflow-y-auto bg-canvas print:static print:overflow-visible print:bg-white"
    >
      <div className="mx-auto max-w-2xl px-4 py-6 sm:py-10 print:max-w-none print:p-0">
        <div className="mb-4 flex items-center justify-between gap-2 print:hidden">
          <Button asChild variant="secondary" leftIcon={<ArrowLeft />}>
            <Link to={`/admin/finance/invoices/${inv.id}`}>{t('Back to invoice')}</Link>
          </Button>
          <Button leftIcon={<Printer />} onClick={() => window.print()}>
            {t('Print receipt')}
          </Button>
        </div>

        <article
          aria-label={t('Payment receipt {{number}}', { number: p.receiptNumber })}
          className="rounded-card border border-line bg-surface p-6 shadow-card sm:p-10 print:rounded-none print:border-0 print:p-0 print:shadow-none"
        >
          <header className="flex flex-col gap-4 border-b border-line pb-6 sm:flex-row sm:items-start sm:justify-between print:flex-row">
            <div className="flex items-start gap-3">
              {school.logoUrl ? (
                <img src={school.logoUrl} alt="" className="size-14 rounded-control object-contain" />
              ) : null}
              <div>
                <p className="text-lg font-semibold text-ink">{school.name}</p>
                {school.address ? (
                  <p className="text-xs whitespace-pre-line text-muted">{school.address}</p>
                ) : null}
                <p className="text-xs text-muted">
                  {[school.phone, school.email].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
            <div className="sm:text-right print:text-right">
              <p className="text-xs font-semibold tracking-widest text-brand-700 uppercase print:text-ink">
                {t('Payment receipt')}
              </p>
              <p className="mt-1 font-mono text-base font-semibold text-ink">{p.receiptNumber}</p>
              <p className="text-xs text-muted">{t('Paid on {{date}}', { date: formatDate(p.paidOn) })}</p>
            </div>
          </header>

          {p.isReversed ? (
            <Alert tone="danger" title={t('This payment was reversed')} className="mt-6">
              {formatDateTime(p.reversedAt)}
              {p.reversalReason ? ` — ${p.reversalReason}` : ''}. {t('This receipt is void.')}
            </Alert>
          ) : null}

          <div className="mt-6 grid gap-6 sm:grid-cols-2 print:grid-cols-2">
            <section aria-labelledby="receipt-from">
              <h2 id="receipt-from" className="mb-2 text-xs font-semibold tracking-wide text-muted uppercase">
                {t('Received from')}
              </h2>
              <p className="font-medium text-ink">{student.name}</p>
              <p className="text-sm text-ink-soft">
                {student.studentCode} · {t('Adm. {{number}}', { number: student.admissionNumber })}
              </p>
              {student.className ? (
                <p className="text-sm text-ink-soft">
                  {sectionLabel(student.className, student.sectionName, ' – ')}
                </p>
              ) : null}
            </section>
            <section aria-labelledby="receipt-for">
              <h2 id="receipt-for" className="mb-2 text-xs font-semibold tracking-wide text-muted uppercase">
                {t('For invoice')}
              </h2>
              <p className="font-mono text-sm font-medium text-ink">{inv.number}</p>
              <p className="text-sm text-ink-soft">{localizePeriodLabel(inv.periodLabel)}</p>
              <p className="text-sm text-ink-soft">{t('Due {{date}}', { date: formatDate(inv.dueDate) })}</p>
            </section>
          </div>

          <div className="mt-8 rounded-card bg-brand-50 p-5 text-center print:border print:border-line print:bg-white">
            <p className="text-xs font-medium tracking-wide text-brand-700 uppercase print:text-ink">
              {t('Amount received')}
            </p>
            <p className="mt-1 text-3xl font-semibold text-ink tabular">{money(p.amount)}</p>
            <p className="mt-1 text-sm text-ink-soft">
              {t(methodLabel[p.method])}
              {p.reference ? ` · ${t('Ref {{reference}}', { reference: p.reference })}` : ''}
            </p>
          </div>

          <dl className="mt-8 divide-y divide-line border-y border-line">
            <Row label={t('Invoice total')} value={<span className="tabular">{money(inv.total)}</span>} />
            <Row label={t('Paid to date')} value={<span className="tabular">{money(inv.amountPaid)}</span>} />
            <Row
              label={t('Balance remaining')}
              value={<span className="tabular">{money(inv.outstanding)}</span>}
            />
            <Row label={t('Invoice status')} value={<InvoiceStatusBadge status={inv.status} />} />
          </dl>

          {p.note ? (
            <p className="mt-4 text-sm text-ink-soft">{t('Note: {{note}}', { note: p.note })}</p>
          ) : null}

          <footer className="mt-10 flex flex-col gap-6 text-xs text-muted sm:flex-row sm:items-end sm:justify-between print:flex-row">
            <p>
              {t('Received by {{name}} · Recorded {{date}}', {
                name: p.receivedBy ?? '—',
                date: formatDateTime(p.createdAt),
              })}
            </p>
            <div className="w-48 border-t border-line-strong pt-1 text-center">{t('Signature & stamp')}</div>
          </footer>
        </article>
      </div>
    </div>,
    document.body,
  )
}
